import type { IVariable } from "@bbj-types/utils";
import { Stack, type StackProps } from "@mui/material";
import { useControllableState } from "@root/utils/hooks/useControllableState";
import { type SetStateAction, useCallback, useMemo, useState } from "react";
import { FormSmsInputContent, type TFormSmsInputContentProps } from "./content";
import { FormSmsInputTemplates, type IFormSmsInputTempaltesProps } from "./templates";
import type { TFormSmsTemplate } from "./types";

export interface IFormSmsProps<Template extends TFormSmsTemplate> {
  value?: string;
  onChange?: (value: string) => void;
  templates?: Template[];
  characterLimit?: number;
  hardCharacterLimit?: boolean;
  variableList?: IVariable[];
  disabled?: boolean;
  slotProps?: {
    root?: Partial<StackProps>;
    inputTemplates?: Partial<Omit<IFormSmsInputTempaltesProps<Template>, "value" | "onChange" | "options">>;
    inputContent?: Partial<Omit<TFormSmsInputContentProps, "value" | "onChange">>;
  };
}

export const FormSms = <Template extends TFormSmsTemplate>({
  value,
  onChange,
  templates,
  characterLimit,
  hardCharacterLimit,
  variableList,
  disabled,
  slotProps,
}: IFormSmsProps<Template>) => {
  const [content, setContent] = useControllableState("", value, onChange);
  const [selectedTemplate, _setSelectedTemplate] = useState<Template | null>(templates?.[0] ?? null);

  const editorValue = useMemo(() => ({ html: content, text: content }), [content]);

  const handleContentChange = useCallback(
    (newValue: { html: string; text: string }) => {
      setContent(newValue.text);
    },
    [setContent]
  );

  const setSelectedTemplate = useCallback(
    (state: SetStateAction<Template | null>) => {
      _setSelectedTemplate((prevState) => {
        const newState = typeof state === "function" ? state(prevState) : state;
        if (newState?._id !== prevState?._id) {
          setContent(newState ? newState.content : "");
        }
        return newState;
      });
    },
    [setContent]
  );

  return (
    <Stack {...slotProps?.root} sx={{ gap: 2, ...slotProps?.root?.sx }}>
      {templates && (
        <FormSmsInputTemplates
          {...slotProps?.inputTemplates}
          value={selectedTemplate}
          onChange={(_, template) => setSelectedTemplate(template)}
          options={templates}
        />
      )}
      <FormSmsInputContent
        {...slotProps?.inputContent}
        characterLimit={characterLimit}
        hardCharacterLimit={hardCharacterLimit}
        value={editorValue}
        onChange={handleContentChange}
        variableList={variableList}
        disabled={disabled}
      />
    </Stack>
  );
};
